import { useRef } from "react";
import { Box, TextField, Typography } from "@mui/material";
import _ from "lodash";
import SuppressionBuilder from "./suppressionBuilder";

const lookupFields = {
	ActivityLog_Heading: { label: "Disposition Values", type: "heading" },
	"metaData.DispositionClassificationId": { label: "Classification ID", type: "text" },
	disposition: { label: "Disposition", type: "text" },
	type: {
		label: "Activity",
		type: "select",
		values: [
			{ name: "Note", label: "Note" },
			{ name: "Dial", label: "Dial" },
			{ name: "SMS", label: "SMS" },
			{ name: "Email", label: "Email" },
			{ name: "SMSApprove", label: "SMSApprove" },
			{ name: "SMSSuppress", label: "SMSSuppress" },
			{ name: "SMSRecycle", label: "SMSRecycle" },
			{ name: "SMSUndeliverable", label: "SMSUndeliverable" },
			{ name: "System", label: "System" },
			{ name: "C3", label: "C3" },
			{ name: "DialApprove", label: "DialApprove" },
			{ name: "DialSuppress", label: "DialSuppress" },
			{ name: "DialRecycle", label: "DialRecycle" }
		]
	},
	timestamp: { label: "Timestamp", type: "date" },
	POC_Fields: { label: "POC Fields", type: "heading" },
	"metaData.metaFieldData.poc.cell": { label: "Is cellphone", type: "boolean" },
	"metaData.metaFieldData.poc.consent": { label: "Has consent", type: "boolean" },
	"metaData.metaFieldData.poc.writtenConsent": { label: "Date of written consent", type: "date" },
	"metaData.metaFieldData.poc.optOut": { label: "Has opted out", type: "boolean" },
	"metaData.metaFieldData.poc.preferred": { label: "Is preferred", type: "boolean" },
	"metaData.metaFieldData.poc.thirdParty": { label: "Is third party", type: "boolean" }
};

const condensedFields = Object.keys(lookupFields).map((x) => ({
	name: x,
	label: lookupFields[x].label,
	type: lookupFields[x].type
}));

export default function ActivityLogSuppression({ formikProps, checkIndex, tenant }) {
	const queryInitialTouch = useRef(false);

	// Ensure checks array exists
	if (!Array.isArray(formikProps.values.checks)) formikProps.setFieldValue("checks", []);

	// Ensure the current check exists
	if (!formikProps.values.checks?.[checkIndex]) {
		const fallback = {
			activityLogQuerySuppression: {
				rawQuery: { combinator: "AND", rules: [] },
				query: null,
				threshold: 1
			}
		};
		const next = [...formikProps.values.checks];
		next[checkIndex] = fallback;
		formikProps.setFieldValue("checks", next);
	}

	const basePath = `checks.${checkIndex}.activityLogQuerySuppression`;
	const rawQueryPath = `${basePath}.rawQuery`;
	const queryPath = `${basePath}.query`;
	const thresholdPath = `${basePath}.threshold`;

	const activityLogQuerySuppression = formikProps.values.checks?.[checkIndex]?.activityLogQuerySuppression ?? {
		rawQuery: { combinator: "AND", rules: [] },
		query: null,
		threshold: 1
	};

	function processFilterArray(items = [], combinator = "AND", isBase = true) {
		const ruleArray = [];
		const ruleGroup = { [combinator.toLowerCase()]: ruleArray };
		for (const item of items) {
			ruleArray.push(processFilterItem(item));
		}

		if (isBase) {
			return {
				and: [
					{ tenant: { eq: tenant } },
					{ activityLogContactId: { eq: "CONTACTIDPLACEHOLDER" } },
					ruleGroup
				]
			};
		} else {
			return ruleGroup;
		}
	}

	function processFilterItem(item = {}) {
		const cpyItem = { ...item };

		if (cpyItem?.field) {
			if (cpyItem.value === true || cpyItem.value === false || cpyItem.value === "true" || cpyItem.value === "false") {
				cpyItem.value = _.isString(cpyItem.value) ? cpyItem.value === "true" : cpyItem.value === true;
			} else if (!Number.isNaN(Number(cpyItem.value)) && cpyItem.value !== "") {
				cpyItem.value = Number(cpyItem.value);
			} else if (typeof cpyItem.value === "string" && !Number.isNaN(Date.parse(cpyItem.value))) {
				cpyItem.value = Date.parse(cpyItem.value);
			}

			if (typeof cpyItem.value === "string") {
				if (cpyItem.operator === "wildcard*") {
					cpyItem.value = `${cpyItem.value.toLowerCase()}*`;
					cpyItem.operator = "wildcard";
				} else if (cpyItem.operator === "*wildcard") {
					cpyItem.value = `*${cpyItem.value.toLowerCase()}`;
					cpyItem.operator = "wildcard";
				} else if (cpyItem.operator === "wildcard") {
					cpyItem.value = cpyItem.value.toLowerCase();
				}
			}

			return { [cpyItem.field]: { [cpyItem.operator]: cpyItem.value } };
		}

		return processFilterArray(cpyItem.rules ?? [], cpyItem.combinator ?? "AND", false);
	}

	function handleQueryChange(query = { rules: [], combinator: "AND" }) {
		if (!queryInitialTouch.current) queryInitialTouch.current = true;
		formikProps.setFieldValue(rawQueryPath, query);
		const formattedQuery = processFilterArray(query.rules ?? [], query.combinator ?? "AND");
		formikProps.setFieldValue(queryPath, formattedQuery);
	}

	function getType(field) {
		return lookupFields[field]?.type ?? "text";
	}

	function getValues(field) {
		return lookupFields[field]?.values ?? [];
	}

	function getOperators(field) {
		const t = lookupFields[field]?.type;
		switch (t) {
			case "date":
				return [
					{ name: "lt", label: "<" },
					{ name: "gt", label: ">" },
					{ name: "lte", label: "<=" },
					{ name: "gte", label: ">=" }
				];
			case "boolean":
			default:
				return [
					{ name: "eq", label: "=" },
					{ name: "ne", label: "!=" }
				];
		}
	}

	return (
		<Box display="flex" flexDirection="column" gap={2}>
			<SuppressionBuilder
				query={activityLogQuerySuppression.rawQuery ?? { combinator: "AND", rules: [] }}
				onQueryChange={handleQueryChange}
				showCombinatorsBetweenRules
				fields={condensedFields}
				getValueEditorType={getType}
				getInputType={getType}
				getValues={getValues}
				getOperators={getOperators}
			/>
			<Box display="flex" flexDirection="column" gap={1} mt={1}>
				<Typography variant="overline" color="text.secondary">
					If the number of Activity Log records matching the query exceeds this threshold, the record will be suppressed.
				</Typography>
				<TextField
					required
					label="Threshold"
					type="number"
					value={activityLogQuerySuppression.threshold ?? ""}
					name={thresholdPath}
					onChange={(e) => {
						const raw = e.target.value;
						formikProps.setFieldValue(thresholdPath, raw === "" ? "" : Number(raw));
					}}
					InputProps={{ inputProps: { min: 0 } }}
				/>
			</Box>
		</Box>
	);
}
